import { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import Sidebar from '../../components/dashboard/Sidebar';
import BotSettingsModal from '../../components/dashboard/BotSettingsModal';
import { DashboardChromeProvider, useDashboardChrome } from '../../hooks/useDashboardChrome';
import { companyName } from '../../utils/botHelpers';
import { apiFetch } from '../../utils/api';

export default function DashboardLayout() {
  return (
    <DashboardChromeProvider>
      <DashboardLayoutInner />
    </DashboardChromeProvider>
  );
}

function DashboardLayoutInner() {
  const navigate = useNavigate();
  const { userData, settingsBotId, closeSettings } = useDashboardChrome();

  const trialDaysLeft = userData?.paymentStatus === 'trial' && userData?.trialEndsAt
    ? Math.max(0, Math.ceil((userData.trialEndsAt - Date.now()) / 86400000))
    : 0;

  const [pendingCoupon, setPendingCoupon] = useState<{ code: string; proposedPlan: string } | null>(null);
  useEffect(() => {
    if (!userData) return;
    apiFetch('/api/coupons/pending-invite').then(r => r.json()).then(d => {
      if (d.success && d.invite) setPendingCoupon(d.invite);
    }).catch(() => {});
  }, [userData]);

  return (
    <>
      <SEO title={`Dashboard | ${companyName}`} description="Manage your bots, view conversations, and track performance." keywords="WhatsApp bot dashboard, chatbot analytics, bot management" type="website" noindex />
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', background: 'var(--bg-page)', fontFamily: "'DM Sans', system-ui, sans-serif" }}>
        {/* Trial countdown banner */}
        {userData?.paymentStatus === 'trial' && userData?.trialEndsAt && (
          <div style={{ background: 'linear-gradient(90deg,#3d35b5,#7c3aed)', color: '#fff', padding: '9px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, fontSize: '13px' }}>
            <span>🎁 <strong>Business Trial</strong> — {trialDaysLeft > 0 ? `${trialDaysLeft} day${trialDaysLeft > 1 ? 's' : ''} left` : 'ends today'}</span>
            <a href="/billing" style={{ color: '#c4b5fd', fontWeight: 600, textDecoration: 'none' }}>Manage →</a>
          </div>
        )}
        {/* WhatsApp verification nudge — accounts created before this check
            existed (or that skipped it during onboarding) never get hard-
            blocked, just reminded, since draft-review alerts, handoff
            notifications etc. all silently go nowhere without it. */}
        {userData?.onboardingCompleted && !userData?.phoneVerified && (
          <div style={{ background: '#d97706', color: '#fff', padding: '9px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, fontSize: '13px' }}>
            <span>⚠️ Your WhatsApp number isn't verified — account notifications (draft-reply reviews, handoff alerts, invoices) can't reach you.</span>
            <a href="/account" style={{ color: '#fff', fontWeight: 600, textDecoration: 'none' }}>Verify now →</a>
          </div>
        )}
        {pendingCoupon && (
          <div style={{ background: '#059669', color: '#fff', padding: '9px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, fontSize: '13px' }}>
            <span>🎁 You have a coupon waiting — {pendingCoupon.code} for the {pendingCoupon.proposedPlan} plan</span>
            <a href="/billing" style={{ color: '#fff', fontWeight: 600, textDecoration: 'none' }}>Apply now →</a>
          </div>
        )}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <Sidebar />
          <Outlet />
          {settingsBotId && (
            <BotSettingsModal
              botId={settingsBotId}
              onClose={closeSettings}
              onSaved={closeSettings}
              onDeleted={() => { closeSettings(); navigate('/dashboard'); }}
            />
          )}
        </div>
      </div>
    </>
  );
}
